import { useNavigate } from "@tanstack/react-router";
import type { Uuid } from "spacetimedb";

import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { useSelectedBrandIndex } from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type TtwPageProps = {
  /** The caller's underwritable entity. Threaded through for id-scoping; the
   * editor resolves the CARD itself, but the navigation carries `?brand=` so it
   * resolves it against the brand this deck is scoped to. */
  underwritableEntity: Uuid;
};

/**
 * "Design your investor page" — the first-draft invitation.
 *
 * Only ever rendered when the owner has NO page yet (`ttwPageRule` gates on it),
 * so there is deliberately no "update an existing page" copy here. Once a page
 * exists the share card becomes the page's home and this card retires; editing
 * is reached from there or from the completed card.
 */
function TtwPageAction({ onSkip }: TtwPageProps & SmbActionCoreProps) {
  const navigate = useNavigate();
  // The dashboard's selected brand, so the composer opens on the same business
  // the deck is showing.
  const brand = useSelectedBrandIndex();

  return (
    <ActionCard category="fundraise">
      <ActionCard.Header>
        <ActionCard.Badge>Fundraise</ActionCard.Badge>
        <ActionCard.DetailsTrigger />
      </ActionCard.Header>
      <ActionCard.Main>
        <ActionCard.Title>Design your investor page</ActionCard.Title>
        <ActionCard.Description>
          Build a public page that tells your story to potential investors, then share the link.
        </ActionCard.Description>
      </ActionCard.Main>
      <ActionCard.Footer>
        <ActionCard.SkipButton onSkip={onSkip} />
        <ActionCard.PrimaryButton
          onClick={() => navigate({ to: "/profile/ttw-editor", search: { brand } })}
        >
          Design page
        </ActionCard.PrimaryButton>
      </ActionCard.Footer>
      <ActionCard.Back title="Why we do this">
        <p>
          Your investor page is what people see when you share your business — a polished, on-brand
          pitch for a "testing-the-waters" raise. Designing it here keeps it tied to your shareable
          card, so the same link always shows your latest page.
        </p>
      </ActionCard.Back>
    </ActionCard>
  );
}

export function createTtwPageAction(
  props: TtwPageProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "ttw-page", canSkip = true, ...rest } = props;
  return {
    id,
    category: "fundraise",
    canSkip,
    render: (core) => <TtwPageAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createTtwPageCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/TtwPageCompleted";

// Semantic gate: once the brand has a card but NO page yet, invite them to
// design one. It retires the moment a page exists.
//
// The `hasTtwPage` half is load-bearing, not tidying. Once a page is saved the
// share card takes over as "your investor page" — same title, same composer
// destination, wearing the page's own hero (see `docs/TTW_SHARE_UX_SPEC.md`).
// Leaving this rule un-gated put three cards in the deck saying the same thing
// with the same button. This card's whole job is "go build a page", so having a
// page is exactly when it should stop asking.
//
// Editing an existing page is reached from the share card and from
// `ttwPageCompletedRule` below; nothing is lost by retiring this one.
export const ttwPageRule = CardRulesEngine.singletonRule({
  id: "ttw-page",
  priority: 21,
  when: CardRulesEngine.and(
    CardRulesEngine.hasShareableBusinessCard,
    CardRulesEngine.not(CardRulesEngine.hasTtwPage),
  ),
  build: (d) => createTtwPageAction({ underwritableEntity: d.underwritableEntityId }),
});

// One completed card per card that has a saved page.
export const ttwPageCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "ttw-page:done",
  forEach: (d) => d.shareableBusinessCards.filter((c) => c.hasTtwPage),
  when: CardRulesEngine.always,
  build: (_, card) =>
    createTtwPageCompleted({ cardId: card.id, requiresAccessCode: card.requiresAccessCode }),
});
